import { useEffect, useState } from "react";
import axios from "axios";
import { Users, IndianRupee, Activity, PieChart as PieIcon, Layers } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import StatCard from "../components/StatCard";
import { PageHeader, SectionHeader } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";
import { GranteeTable } from "../components/grantee/GranteeTable";
import { POWorkload } from "../components/grantee/POWorkload";
import { MeasTracking } from "../components/grantee/MeasTracking";
import { IndicatorDetails } from "../components/grantee/IndicatorDetails";
import { getThemeDotColor } from "../utils/dynamicColors";

import { fmtCr } from "../utils/format";
import { applyUplift } from "../utils/sanctionedUplift";

// Fallback palette for the "Grantee Type" pie (Tata-red family + accents)
const GRANT_TYPE_COLORS = ["#B71C1C", "#E57373", "#F59E0B", "#059669", "#1E40AF", "#7C3AED", "#64748b", "#0891b2"];

function DonutChart({ data, title, icon: Icon, centerLabel, centerValue, colorFn, testId }) {
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#B71C1C]" />
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      {total === 0 ? (
        <div className="h-56 flex items-center justify-center text-xs text-slate-500">No data</div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0" style={{ width: 180, height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="count" nameKey="label" cx="50%" cy="50%"
                  innerRadius={52} outerRadius={80} paddingAngle={1}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={colorFn(d.label, i)} stroke="#fff" strokeWidth={1.5} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                  formatter={(v, n) => [`${v} (${((v / total) * 100).toFixed(1)}%)`, n]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-xl font-bold text-slate-900">{centerValue ?? total}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">{centerLabel}</div>
            </div>
          </div>
          <ul className="flex-1 min-w-0 space-y-1.5 text-xs">
            {data.map((d, i) => {
              const pct = ((d.count / total) * 100).toFixed(1);
              return (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colorFn(d.label, i) }} />
                  <span className="text-slate-700 flex-1 truncate" title={d.label}>{d.label}</span>
                  <span className="text-slate-500 tabular-nums whitespace-nowrap">
                    {d.count} <span className="text-slate-400">({pct}%)</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function GranteeOverview() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  const [summary, setSummary] = useState(null);
  const [granteeList, setGranteeList] = useState({ data: [], total: 0 });
  const [indicators, setIndicators] = useState({ data: [], total: 0 });
  const [page, setPage] = useState(1);
  const [indPage, setIndPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedGrantees, setExpandedGrantees] = useState({});
  const [expandLoading, setExpandLoading] = useState({});
  const [indTypeFilter, setIndTypeFilter] = useState("Activity");
  const [poWorkload, setPoWorkload] = useState([]);
  const [measTracking, setMeasTracking] = useState([]);
  const [pendingExpanded, setPendingExpanded] = useState({});
  const [pendingLoading, setPendingLoading] = useState({});
  const [pieData, setPieData] = useState({ by_theme: [], by_grant_type: [] });

  const sf = filterOptions.data_sources?.fluxx || filterOptions.snowflake_connected;
  const params = buildDashboardParams(filters, { view_mode: viewMode });

  const togglePendingPO = async (po) => {
    if (pendingExpanded[po] !== undefined) {
      setPendingExpanded(prev => { const n = { ...prev }; delete n[po]; return n; });
      return;
    }
    setPendingLoading(prev => ({ ...prev, [po]: true }));
    try {
      const res = await axios.get(`${API}/grantee/meas-pending-grants`, { params: { po } });
      setPendingExpanded(prev => ({ ...prev, [po]: res.data }));
    } catch (e) { console.error(e); }
    finally { setPendingLoading(prev => ({ ...prev, [po]: false })); }
  };

  const toggleGranteeExpand = async (grantee, grantCount, theme) => {
    if (expandedGrantees[grantee] !== undefined) {
      setExpandedGrantees(prev => { const n = { ...prev }; delete n[grantee]; return n; });
      return;
    }
    if (grantCount < 1) return;
    setExpandLoading(prev => ({ ...prev, [grantee]: true }));
    try {
      const res = await axios.get(`${API}/grantee/grants-detail`, {
        params: { ...buildDashboardParams(filters, { theme: theme || undefined, status: filters.status || undefined, view_mode: viewMode }), grantee },
      });
      setExpandedGrantees(prev => ({ ...prev, [grantee]: res.data }));
    } catch (e) { console.error(e); }
    finally { setExpandLoading(prev => ({ ...prev, [grantee]: false })); }
  };

  useEffect(() => {
    setLoading(true);
    setGlobalLoading(true);
    Promise.all([
      axios.get(`${API}/grantee/summary`, { params }),
      axios.get(`${API}/grantee/list`, { params: { ...params, page, per_page: 20 } }),
      sf ? axios.get(`${API}/grantee/indicators`, { params: { ...params, page: indPage, per_page: 30, indicator_type: indTypeFilter } }) : Promise.resolve({ data: { data: [], total: 0 } }),
      axios.get(`${API}/grantee/po-workload`, { params: buildDashboardParams(filters, { trust: filters.trust }) }).catch(() => ({ data: [] })),
      axios.get(`${API}/grantee/meas-tracking`, { params: buildDashboardParams(filters, { trust: filters.trust, status: filters.status }) }).catch(() => ({ data: [] })),
      sf ? axios.get(`${API}/grantee/pie-data`, { params }).catch(() => ({ data: { by_theme: [], by_grant_type: [] } })) : Promise.resolve({ data: { by_theme: [], by_grant_type: [] } }),
    ]).then(([sRes, lRes, iRes, poRes, mtRes, pieRes]) => {
      setSummary(sRes.data);
      setGranteeList(lRes.data);
      setIndicators(iRes.data);
      setPoWorkload(poRes.data);
      setMeasTracking(mtRes.data);
      setPieData(pieRes.data);
    }).catch(console.error).finally(() => { setLoading(false); setGlobalLoading(false); });
  }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode, page, indPage, indTypeFilter]);

  const totalGranteeThemePairs = pieData.by_theme.reduce((s, d) => s + (d.count || 0), 0);
  const totalDistinctGrantees   = pieData.by_grant_type.reduce((s, d) => s + (d.count || 0), 0);

  return (
    <div data-testid="grantee-page" className="space-y-6">
      <PageHeader title="Grantee Overview" subtitle="Grant Health Score, KPI details, targets vs actuals" badge="05" />

      {/* Hero Stats — Grantees, Grants, Sanctioned Amount */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard title="Total Grantees" value={summary?.total_grantees} icon={Users} color="red" loading={loading} />
        <StatCard title="Total Grants" value={summary?.active_grants} icon={Activity} color="blue" loading={loading} />
        <StatCard title="Total Sanctioned" value={summary?.total_disbursed_cr != null ? fmtCr(applyUplift(summary.total_disbursed_cr, filters, viewMode, null, null)) : null}
          icon={IndianRupee} color="green" loading={loading} />
      </div>

      {/* Two donut breakdowns: Grantees by Theme + Grants by Grant Type */}
      {sf && (pieData.by_theme.length > 0 || pieData.by_grant_type.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DonutChart
            data={pieData.by_theme}
            title="Grantees by Theme"
            icon={Layers}
            centerLabel="Grantee-Theme pairs"
            centerValue={totalGranteeThemePairs}
            colorFn={(label) => getThemeDotColor(label)}
            testId="grantees-by-theme-chart"
          />
          <DonutChart
            data={pieData.by_grant_type}
            title="Grantees by Type"
            icon={PieIcon}
            centerLabel="Total Grantees"
            centerValue={totalDistinctGrantees}
            colorFn={(label, i) => GRANT_TYPE_COLORS[i % GRANT_TYPE_COLORS.length]}
            testId="grants-by-type-chart"
          />
        </div>
      )}

      {/* Grantee Table (drill-down only — FY trend removed) */}
      <GranteeTable
        granteeList={granteeList}
        page={page} setPage={setPage}
        expandedGrantees={expandedGrantees}
        expandLoading={expandLoading}
        toggleGranteeExpand={toggleGranteeExpand}
      />

      {/* PO Workload */}
      <POWorkload poWorkload={poWorkload} />

      {/* MEAS Data Submission Tracking & Indicator Details hidden per stakeholder request (Jul 2026).
          Preserved as dead code so they can be re-enabled later.
      <MeasTracking
        measTracking={measTracking}
        pendingExpanded={pendingExpanded}
        pendingLoading={pendingLoading}
        togglePendingPO={togglePendingPO}
      />
      <IndicatorDetails
        sf={sf}
        indicators={indicators}
        indPage={indPage} setIndPage={setIndPage}
        indTypeFilter={indTypeFilter} setIndTypeFilter={setIndTypeFilter}
        API={API}
        filters={filters}
      />
      */}
    </div>
  );
}
